import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment";
import { useQuery } from "react-query";
import { useRouteMatch } from "react-router";
import { getName } from "country-list";
import Loading from "../../../components/Loading";
import Error from "../../../components/Error";
import { mutateErrors, urlReplacer } from "../../../utils";
import { useAuth } from "../../auth/hooks/useAuth";
import useUserQueries from "../hooks/useUserQueries";
import { User } from "../types";
import { UserAccountProps } from "./index";
import { DATE_FORMATS } from "../../../constants";
import { UserRole } from "../enums/UserRole";

const useUserAccountProvider = function (_user: User, onUpdate: Function) {
  const { path: matchedPath } = useRouteMatch();
  const [user, setUser] = useState<User>(_user);
  const { user: authUser, profile: authUserProfile } = useAuth();

  useEffect(() => setUser(_user), [_user]);

  const {
    fetchUpdateProfile,
    fetchUpdateMyProfile,
    uploadMyAccountPhoto,
    uploadUserPhoto,
    fetchMyAccountProfile,
    fetchUserProfile,
  } = useUserQueries();

  const userIsAuthUser = useMemo(() => authUser.id === user.id, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const path = useMemo(() => urlReplacer(matchedPath, { id: user.id }), [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadPhotoQuery = useCallback(
    (file) => {
      return userIsAuthUser
        ? uploadMyAccountPhoto(file)
        : uploadUserPhoto(user.id, file);
    },
    [userIsAuthUser, user],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfileQuery = useCallback(() => {
    return userIsAuthUser ? fetchMyAccountProfile() : fetchUserProfile(user.id);
  }, [userIsAuthUser, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAccountQuery = useCallback(
    (data) => {
      let accountData = data;

      switch (user.role) {
        case 4:
          accountData = {
            ...data,
            studentProfile: {
              ...data.studentProfile,
              birth_date: moment(data.studentProfile.birth_date).format(
                DATE_FORMATS.backEnd,
              ),
              country: data.studentProfile.country.code,
              citizenship: data.studentProfile.citizenship.code,
            },
          };
          break;
      }

      return userIsAuthUser
        ? fetchUpdateMyProfile(accountData)
        : fetchUpdateProfile(user.id, accountData);
    },
    [userIsAuthUser, user],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const userProfile = useQuery<any>(`profile_${user.id}`, fetchProfileQuery, {
    enabled: !userIsAuthUser && user && [UserRole.Agent, UserRole.School, UserRole.Student, UserRole.ServiceProvider, UserRole.Teacher].includes(user.role),
  });

  const profile = useMemo(
    () => (userIsAuthUser ? authUserProfile : userProfile),
    [userIsAuthUser, user, authUserProfile, userProfile],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const setPhoto = useCallback(
    (photo) => {
      setUser((prevState) => ({
        ...prevState,
        photo,
      }));
    },
    [user, onUpdate],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const getParsedProfile = useCallback<User | any>(() => {
    let parsedProfile: any = {
      name: user.name,
      email: user.email,
      phone: user.phone,
    };
    if ([UserRole.Owner, UserRole.Admin, UserRole.Support].includes(user.role))
      return parsedProfile;

    if (!profile || !profile.data || !profile.data.data) return undefined;

    let _profile = profile.data.data;

    switch (user.role) {
      case UserRole.Agent:
        parsedProfile.agentProfile = {
          ..._profile,
        };
        break;
      case UserRole.School:
        parsedProfile.schoolProfile = {
          ..._profile,
        };
        break;
      case UserRole.Student:
        parsedProfile.studentProfile = {
          city: _profile.city,
          birth_date:
            _profile.birth_date &&
            moment(_profile.birth_date).format(DATE_FORMATS.backEnd),
          country: _profile.country && {
            code: _profile.country,
            name: getName(_profile.country),
          },
          citizenship: _profile.country && {
            code: _profile.citizenship,
            name: getName(_profile.citizenship),
          },
        };
        break;
      case UserRole.Teacher:
        parsedProfile.teacherProfile = {
          ..._profile,
        };
        break;
    }

    return parsedProfile;
  }, [user, profile]);

  return {
    path,
    user,
    profile,
    userIsAuthUser,
    actions: {
      getParsedProfile,
      onUpdate,
      setPhoto,
      uploadPhotoQuery,
      updateAccountQuery,
    },
  };
};

export const useUserAccount = function () {
  return useContext(UserAccountContext);
};

export const UserAccountContext = React.createContext<
  Partial<ReturnType<typeof useUserAccountProvider>>
>({});

export const UserAccountProvider: FC<UserAccountProps> = ({
  user,
  onUpdate,
  children,
}) => {
  const provider = useUserAccountProvider(user, onUpdate);

  if (provider.profile.isLoading || provider.profile.isFetching)
    return <Loading />;

  if (provider.profile.isError) {
    const { message } = mutateErrors(provider.profile.error);
    return <Error text={message} />;
  }

  return (
    <UserAccountContext.Provider value={provider}>
      {children}
    </UserAccountContext.Provider>
  );
};
